import { FirebaseError } from 'firebase/app';

import BaseApiResponseError from '../api/http/BaseApiResponseError';

/**
 * The one field a Sentry tag can carry that says WHICH failure this was.
 *
 * Five reporters — the session guard's, `AuthProvider`'s three, and the socket
 * initializer's — each wrote `error instanceof Error ? error.name : typeof error`, and
 * both of those branches are CONSTANTS for the two error families that actually reach
 * them:
 *
 *   * the Firebase SDK hardcodes `name = 'FirebaseError'` for every code it mints, so
 *     `auth/network-request-failed` — a dropped connection, over before the user notices —
 *     and `auth/user-token-expired` — a session that is genuinely gone — arrived as the
 *     same event while asking for opposite responses;
 *   * `BaseApiResponseError` deliberately does NOT extend `Error`, so every API refusal
 *     was reported as the literal string `"object"`, discarding the status and the
 *     machine-readable code the class already exposes.
 *
 * Deliberately still SANITIZED, which is what those reporters wanted from `name` in the
 * first place: a code and a status are the smallest things that tell the cases apart, and
 * the rejected value can be an AxiosError carrying a Bearer token. No message, no payload,
 * no claims, no headers.
 *
 * Imported by PATH, never through `utils/index.ts`: `api/axios.ts` is one of its callers
 * and its spec mocks that barrel wholesale.
 */
export function describeErrorForTag(error: unknown) {
  if (error instanceof FirebaseError) return error.code;
  if (error instanceof BaseApiResponseError) {
    return error.code
      ? `http/${error.status}:${error.code}`
      : `http/${error.status}`;
  }
  if (error instanceof Error) return error.name;
  return typeof error;
}
